import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
  CFormFeedback,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLockLocked, cilLockUnlocked } from '@coreui/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import httpClient from 'src/api/http-client';
import endpoints from 'src/api/endpoint';

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  useEffect(() => {
    if (!email) {
      navigate('/forget-password');
    }
  }, [email, navigate]);

  const formik = useFormik({
    initialValues: { password: '', confirm_password: '' },
    validationSchema: Yup.object({
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters'),
      confirm_password: Yup.string()
        .required('Confirm Password is required')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await httpClient.post(endpoints.auth.resetPassword(), {
          email: email,
          password: values.password,
        });
        toast.success('Password reset successfully! Please login.');
        setTimeout(() => navigate('/login'), 2000);
      } catch (error) {
        error.response?.data?.message?.split(',').forEach((element) => {
          toast.error(element);
        });
      }
      setLoading(false);
    },
  });

  return (
    <div
      className="min-vh-100 d-flex flex-row align-items-center"
      style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' }}
    >
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={7} lg={5}>
            <CCard
              className="border-0"
              style={{ borderRadius: '24px', boxShadow: '0 15px 35px rgba(0,0,0,0.08)' }}
            >
              <CCardBody className="p-5">
                <div className="d-flex justify-content-center mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10 text-primary"
                    style={{ width: '70px', height: '70px' }}
                  >
                    <CIcon icon={cilLockLocked} style={{ width: '32px', height: '32px' }} />
                  </div>
                </div>

                <div className="text-center mb-4">
                  <h3 className="fw-bold mb-2 text-dark">Create New Password</h3>
                  <p className="text-muted" style={{ fontSize: '0.95rem' }}>
                    Enter a strong password to secure your account.
                  </p>
                </div>

                <CForm onSubmit={formik.handleSubmit}>
                  <CInputGroup
                    className="mb-4 shadow-sm"
                    style={{ borderRadius: '12px', overflow: 'hidden' }}
                  >
                    <CFormInput
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New Password"
                      className="border-end-0 py-2 px-3 bg-white"
                      style={{ boxShadow: 'none' }}
                      invalid={!!(formik.touched.password && formik.errors.password)}
                      {...formik.getFieldProps('password')}
                    />
                    <CInputGroupText
                      className="bg-white border-start-0 px-3 text-muted"
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <CIcon icon={showPassword ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                    {!!(formik.touched.password && formik.errors.password) && (
                      <CFormFeedback invalid className="px-3 pb-2 bg-white w-100">
                        {formik.errors.password}
                      </CFormFeedback>
                    )}
                  </CInputGroup>

                  <CInputGroup
                    className="mb-4 shadow-sm"
                    style={{ borderRadius: '12px', overflow: 'hidden' }}
                  >
                    <CFormInput
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm New Password"
                      className="border-end-0 py-2 px-3 bg-white"
                      style={{ boxShadow: 'none' }}
                      invalid={
                        !!(formik.touched.confirm_password && formik.errors.confirm_password)
                      }
                      {...formik.getFieldProps('confirm_password')}
                    />
                    <CInputGroupText
                      className="bg-white border-start-0 px-3 text-muted"
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      title={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      <CIcon icon={showConfirmPassword ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                    {!!(formik.touched.confirm_password && formik.errors.confirm_password) && (
                      <CFormFeedback invalid className="px-3 pb-2 bg-white w-100">
                        {formik.errors.confirm_password}
                      </CFormFeedback>
                    )}
                  </CInputGroup>

                  <div className="d-grid gap-2 mt-4">
                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      className="py-2 text-white fw-semibold shadow-sm"
                      style={{ borderRadius: '12px', fontSize: '1.05rem' }}
                    >
                      {loading ? <CSpinner component="span" size="sm" className="me-2" /> : null}
                      Reset Password
                    </CButton>
                  </div>
                </CForm>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default ResetPassword;
